// MoveDialog .js                   Move dialog (choose destination folder)

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import CrumbsGrid from "../Gallery/CrumbsGrid";
import CreateFolderInline from './CreateFolderInline';

export default function MoveDialog({
    open,
    path,          // масив [{id, name}], поточний шлях
    authed,
    folders,       // список підпапок поточної папки
    enterFolder,   // (folder) => void — перейти в підпапку
    onCancel,
    onConfirm,     // (targetFolderId: string) => void
    excludeIds = [], // масив id папок, які НЕ показувати як варіанти
    createFolder,
}) {
    const canGoUp = path.length > 1;
    const currentFolderId = String(path[path.length - 1]?.id || '');

    // набір для швидкого фільтру
    const excludeSet = useMemo(() => new Set([currentFolderId, ...excludeIds.map(String)]), [excludeIds, currentFolderId]);

    // список відфільтрованих папок для відображення
    const visibleFolders = useMemo(
        () => folders.filter(f => !excludeSet.has(String(f.id))),
        [folders, excludeSet]
    );

    const goUp = () => {
        if (!canGoUp) return;
        // батько — передостанній елемент шляху
        const parent = path[path.length - 2];
        // parent має {id, name} — цього досить, якщо enterFolder приймає такий же shape
        enterFolder(parent);
    };

    const chooseThis = () => onConfirm(currentFolderId);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-xl w-full max-w-xl">
                <div className="p-4 border-b flex items-center justify-between">
                    <div className="font-semibold text-sm">Перемістити до папки {String(path?.[path.length - 1]?.name || '')}</div>
                    <button type="button" className="text-sm px-2 py-1 border rounded" onClick={onCancel}>✕</button>
                </div>

                <div className="p-4">
                    {/* панель навігації */}
                    <div className="flex items-center gap-2 mb-3">
                        <button
                            type="button"
                            className="px-2 py-1 border rounded disabled:opacity-50"
                            onClick={goUp}
                            disabled={!canGoUp}
                            title="Вгору на рівень"
                        >
                            ↑ Вгору
                        </button>
                        <div className="text-sm text-gray-600 dark:text-gray-300 truncate flex items-center">
                            {/* {path.map(f => f.name).join(' / ')} */}
                            <CrumbsGrid
                                path={path}
                                authed={authed}
                                folders={folders}
                                enterFolder={enterFolder}
                            />
                        </div>
                    </div>

                    {/* список підпапок поточної папки */}
                    <ul className="divide-y max-h-80 overflow-auto rounded-lg border">
                        {visibleFolders.map(f => (
                            <li key={f.id} className="py-2 px-3 flex items-center justify-between">
                                <button
                                    type="button"
                                    className="text-left flex-1 hover:underline truncate"
                                    onClick={() => enterFolder(f)}
                                    title="Зайти в цю папку"
                                >
                                    📁 {f.name}
                                </button>
                                {f.modifiedTime && (
                                    <span className="text-xs text-gray-500">{String(f.modifiedTime).slice(0, 10)}</span>
                                )}
                            </li>
                        ))}
                        {visibleFolders.length === 0 && (
                            <li className="py-6 text-center text-sm text-gray-500">Папок немає</li>
                        )}
                    </ul>

                    {/* дії знизу */}
                    <div className="mt-4 flex items-center justify-between gap-2">
                        <button
                            type="button"
                            className="px-3 py-2 border rounded"
                            onClick={chooseThis}
                            title="Обрати поточну папку як місце призначення"
                        >
                            Перемістити у {String(path?.[path.length - 1]?.name || '')}
                        </button>

                        {/* інлайн-створення папки */}
                        <CreateFolderInline onCreate={createFolder} />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---- propTypes для MoveDialog ----
MoveDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    path: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string.isRequired })).isRequired,
    authed: PropTypes.bool.isRequired,
    folders: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
        })
    ).isRequired,
    enterFolder: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired, // (targetFolderId: string) => void
    excludeIds: PropTypes.arrayOf(PropTypes.string),
    createFolder: PropTypes.func.isRequired,
};